import { SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { FilterFieldMeta, FilterGroup } from '@aurora/contracts';
import { Button } from '../../components/ui';
import { FilterPanel } from './filter-panel';
import { EMPTY_GROUP, incompletePaths, withoutEmptyGroups } from './list-filters';

/**
 * The ticket list's advanced filter, edited as a draft.
 *
 * Every edit used to go straight into the URL and the list request, so a
 * condition was applied while it was still being built: "+ Condition" emptied
 * the table, choosing a date field replaced it with an error, and every
 * keystroke in a value box was a request. Now nothing reaches the list until
 * "Apply", and Apply waits until every condition says something complete —
 * the ones that do not are marked, with what is missing.
 */

/**
 * Fields a ticket list's conditions are not offered on. Spam is excluded from
 * every list, so "Spam is Yes" could only ever be empty; the others are stored
 * as internal ids nobody can see, with nothing to look a name up in.
 */
const NOT_OFFERED = new Set(['is_spam', 'workspace_id', 'category_id', 'sla_policy_id', 'parent_id']);

export function offerTicketField(field: FilterFieldMeta): boolean {
  return !NOT_OFFERED.has(field.field);
}

export function ListFilterBuilder({
  fields,
  applied,
  onApply,
  onClose,
}: {
  fields: FilterFieldMeta[];
  /** What the list is filtered by now. */
  applied: FilterGroup;
  onApply: (next: FilterGroup) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<FilterGroup>(applied);
  const appliedJson = JSON.stringify(applied);
  const lastApplied = useRef(appliedJson);

  // Follow a change made outside the builder — a chip removed, Reset, another
  // view — unless the draft holds edits of its own.
  useEffect(() => {
    if (appliedJson === lastApplied.current) return;
    setDraft((current) => (JSON.stringify(current) === lastApplied.current ? applied : current));
    lastApplied.current = appliedJson;
  }, [appliedJson, applied]);

  const fieldsByKey = useMemo(() => new Map(fields.map((field) => [field.field, field])), [fields]);
  const cleaned = withoutEmptyGroups(draft);
  const missing = incompletePaths(cleaned, fieldsByKey).length;
  const dirty = JSON.stringify(cleaned) !== JSON.stringify(withoutEmptyGroups(applied));

  const apply = (event?: FormEvent) => {
    event?.preventDefault();
    if (missing > 0 || !dirty) return;
    onApply(cleaned);
  };

  const clear = () => {
    setDraft(EMPTY_GROUP);
    onApply(EMPTY_GROUP);
  };

  const cancel = () => {
    setDraft(applied);
    onClose();
  };

  return (
    <section
      aria-labelledby="ticket-filter-heading"
      className="shrink-0 border-b border-line bg-sunken/60 px-4 py-3 sm:px-6"
      onKeyDown={(event) => {
        // Escape closes the panel, unless it was meant for a menu inside it.
        if (event.key === 'Escape' && !event.defaultPrevented) cancel();
      }}
    >
      <form onSubmit={apply} noValidate>
        <div className="mb-2 flex items-center gap-2">
          <SlidersHorizontal aria-hidden className="size-3.5 text-ink-subtle" />
          <h2 id="ticket-filter-heading" className="text-reading font-semibold text-ink">
            Advanced filter
          </h2>
          <span className="text-mini text-ink-subtle">Nothing changes until you apply it.</span>
          <Button size="icon" variant="ghost" className="ms-auto size-7" onClick={cancel} aria-label="Close the filter">
            <X aria-hidden className="size-3.5" />
          </Button>
        </div>

        <div className="max-w-5xl">
          <FilterPanel fields={fields} value={draft} onChange={setDraft} flagIncomplete offerField={offerTicketField} />
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          {missing > 0 && (
            <p role="status" className="me-auto text-mini text-caution-ink">
              {missing === 1
                ? 'One condition is not finished. Choose its value or remove it.'
                : `${missing} conditions are not finished. Choose their values or remove them.`}
            </p>
          )}
          {applied.nodes.length > 0 && (
            <Button size="sm" variant="ghost" onClick={clear}>
              Clear all
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={cancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" type="submit" disabled={missing > 0 || !dirty}>
            Apply filter
          </Button>
        </div>
      </form>
    </section>
  );
}
